import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  InputNumber,
  Upload,
  Dropdown,
  Progress,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExportOutlined,
  ImportOutlined,
  DownloadOutlined,
  UploadOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import feeService, { Fee, FeeListParams, FilterOption } from '../services/fee';
import documentService from '../services/document';
import mediaService from '../services/media';
import { NATION_OPTIONS } from '../config/nations';
import { SERVICE_TYPE_OPTIONS } from '../config/service-types';
import { SERVICE_TYPE_MAP } from '../config/service-type-map';
import ColumnSelector from '../components/ColumnSelector';
import userPreferenceService from '../services/userPreference';
import usePermissions from '../hooks/usePermissions';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { filterActionColumnByAccess, hasAnyRowActionAccess } from '../utils/action-permissions';
import ListHelpButton from '../components/ListHelpButton';

const { Option } = Select;
const { TextArea } = Input;

// 简化的文档显示组件（使用后端返回的 documents_info）
interface SimpleDocumentsDisplayProps {
  documentsInfo: Array<{
    id?: number;
    name?: string;
    file_name?: string;
    media_files_info?: Array<{
      id?: number;
      name?: string;
      file_name?: string;
      size?: number;
      file_size?: number;
      type?: string;
      file_type?: string;
      mime_type?: string;
      path?: string;
    }>;
  }>;

}

const SimpleDocumentsDisplay: React.FC<SimpleDocumentsDisplayProps> = ({
  documentsInfo,

}) => {
  const [expanded, setExpanded] = useState(false);

  // 截断展示文本，避免文件名过长撑破布局
  const truncate = (text?: string, maxLength: number = 16) => {
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength) + '…' : text;
  };

  const renderDocumentTag = (doc: any, index: number) => {
    // 获取文档包含的所有文件
    const menuItems: Array<{
      key: string;
      label: React.ReactNode;
      onClick: () => void;
    }> = [];

    // 从 media_files_info 获取所有文件
    if (doc.media_files_info && Array.isArray(doc.media_files_info)) {
      doc.media_files_info.forEach((media: any, mediaIndex: number) => {
        const mediaFileName = media.name || media.file_name;
        if (mediaFileName && media.id && doc.id) {
          const downloadUrl = mediaService.getMediaUrl(media);
          menuItems.push({
            key: `${doc.id}-media-${mediaIndex}`,
            label: (
              <span
                style={{ color: 'inherit', display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  mediaService.downloadMedia(media.id, mediaFileName);
                }}
              >
                <DownloadOutlined />
                {mediaFileName}
              </span>
            ),
            onClick: () => {} // Handled by <span> tag
          });
        }
      });
    }

    const totalFiles = menuItems.length;
    const docName = doc.name || doc.file_name || `文档${doc.id}`;
    const displayName = truncate(docName);

    if (totalFiles === 0) {
      return (
        <Tag key={`doc-${index}`} color="default" style={{ marginBottom: 2 }}>
          {docName} (无文件)
        </Tag>
      );
    }

    return (
      <Dropdown key={`doc-${index}`} menu={{ items: menuItems }} trigger={['click']}>
        <Tag
          color="blue"
          style={{ marginBottom: 2, cursor: 'pointer', display: 'inline-block', maxWidth: 220 }}
          title={`${docName} - 包含${totalFiles}个文件，点击查看全部`}
        >
          <span
            style={{
              display: 'inline-block',
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom',
            }}
            title={docName}
          >
            {displayName}
          </span>
          <span>{` (${totalFiles}) ▼`}</span>
        </Tag>
      </Dropdown>
    );
  };

  const displayDocs = expanded ? documentsInfo : documentsInfo.slice(0, 3);
  const hasMore = documentsInfo.length > 3;

  return (
    <Space wrap>
      {displayDocs.map(renderDocumentTag)}
      {hasMore && (
        <Tag
          color="default"
          style={{ cursor: 'pointer', marginBottom: 2 }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '收起' : `+${documentsInfo.length - 3}更多`}
        </Tag>
      )}
    </Space>
  );
};


const FeeManagement: React.FC = () => {
  useDocumentTitle('最新报价');
  const permissions = usePermissions();
  const canCreate = permissions.hasActionAccess('fee-management', 'create');
  const canEdit = permissions.hasActionAccess('fee-management', 'edit');
  const canDelete = permissions.hasActionAccess('fee-management', 'delete');
  const canExport = permissions.hasActionAccess('fee-management', 'export');
  const canImport = permissions.hasActionAccess('fee-management', 'import');
  const canViewDetail = permissions.hasActionAccess('fee-management', 'view_details');
  const hasRowActionAccess = hasAnyRowActionAccess(permissions, 'fee-management', ['view_details', 'edit', 'delete']);
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [fees, setFees] = useState<Fee[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subServiceOptions, setSubServiceOptions] = useState<Array<{ value: string; label: string }>>([]);

  // 从 URL 参数初始化分页状态
  const [pagination, setPagination] = useState({
    current: parseInt(urlSearchParams.get('page') || '1', 10),
    pageSize: parseInt(urlSearchParams.get('pageSize') || '10', 10),
    total: 0,
  });
  const [searchParams, setSearchParams] = useState<FeeListParams>({});
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFee, setEditingFee] = useState<Fee | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const watchedService = Form.useWatch('service', form);

  // 根据业务类型动态生成子业务选项
  useEffect(() => {
    const map = SERVICE_TYPE_MAP[watchedService || ''] || {};
    const options = Object.entries(map).map(([value, label]) => ({ value, label }));
    setSubServiceOptions(options);

    const currentSub = form.getFieldValue('sub_service');
    if (currentSub && !options.find(opt => opt.value === currentSub)) {
      form.setFieldsValue({ sub_service: undefined });
    }
  }, [watchedService, form]);

  // 导入导出相关状态
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
    preserveSelectedRowKeys: true,
  }), [selectedRowKeys]);

  // 筛选状态
  const [filtersState, setFilters] = useState({
    nation: '',
    service: '',
    submit_channel: '',
  });

  // 文档列表状态
  const [documentOptions, setDocumentOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [documentLoading, setDocumentLoading] = useState(false);

  // 不再需要文档详情缓存，直接使用后端返回的 documents_info

  // 使用 useMemo 稳定 filters 对象引用
  const filters = useMemo(() => filtersState, [
    filtersState.nation,
    filtersState.service,
    filtersState.submit_channel,
  ]);

  // 用于跟踪是否已经初始化
  const isInitialized = useRef(false);

  // 动态筛选选项
  const [filterOptions, setFilterOptions] = useState({
    nations: [] as FilterOption[],
    services: [] as FilterOption[],
    submit_channels: [] as FilterOption[],
  });

  // 业务类型选项
  const serviceTypeOptions = SERVICE_TYPE_OPTIONS;

  // 递交渠道选项
  const channelOptions = [
    { value: '在线递交', label: '在线递交' },
    { value: '线下递交', label: '线下递交' },
    { value: '代理递交', label: '代理递交' },
  ];

  // 制度选项
  const categoryOptions = [
    { value: '一类制', label: '一类制' },
    { value: '多类制', label: '多类制' },
  ];

  // 国家选项
  const nationOptions = NATION_OPTIONS;

  // 获取筛选选项
  const fetchFilterOptions = async () => {
    try {
      const response = await feeService.getFilterOptions();
      if (response.success) {
        setFilterOptions({
          nations: response.data.nations || [],
          services: response.data.services || [],
          submit_channels: response.data.submit_channels || [],
        });
      }
    } catch (error) {
      console.error('获取筛选选项失败:', error);
    }
  };

  // 获取文档选项
  const fetchDocumentOptions = async (search?: string) => {
    try {
      setDocumentLoading(true);
      const response = await documentService.getDocumentsList(search);
      if (response.success) {
        setDocumentOptions(response.data);
      }
    } catch (error) {
      console.error('获取文档选项失败:', error);
    } finally {
      setDocumentLoading(false);
    }
  };

  // 处理文档搜索
  const handleDocumentSearch = (value: string) => {
    if (value.length >= 2 || value.length === 0) {
      fetchDocumentOptions(value);
    }
  };

  // 处理筛选变化
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // 重置筛选
  const handleResetFilters = () => {
    setFilters({
      nation: '',
      service: '',
      submit_channel: '',
    });
    setSearchText('');
  };



  // 获取费用数据
  const fetchFees = async (params?: FeeListParams) => {
    try {
      setLoading(true);

      // 构建请求参数
      const requestParams: any = {
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        ...searchParams,
        ...params,
      };

      // 添加筛选参数
      Object.keys(filters).forEach(key => {
        if (filters[key as keyof typeof filters]) {
          requestParams[key] = filters[key as keyof typeof filters];
        }
      });

      const response = await feeService.getFees(requestParams);

      if (response.success) {
        setFees(response.data.data);
        // 只更新 total 和 current，避免触发分页循环
        setPagination(prev => ({
          ...prev,
          current: response.data.current_page,
          total: response.data.total,
          // 不更新 pageSize，因为它应该由用户偏好控制
        }));
      }
    } catch (error) {
      console.error('获取费用数据失败:', error);
      message.error('获取费用数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    loadPageSizeSettings();
    fetchDocumentOptions(); // 初始化时加载文档选项
    // 延迟标记初始化完成，确保分页状态已设置
    setTimeout(() => {
      isInitialized.current = true;
      // 初始化完成后获取第一次数据
      fetchFees();
    }, 0);
  }, []);

  // 搜索、筛选变化时重置到第一页
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current !== 1) {
      const newPagination = { ...pagination, current: 1 };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      fetchFees();
    }
  }, [searchText, filters]);

  // 分页变化时获取数据
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    if (pagination.current > 0 && pagination.pageSize > 0) {
      fetchFees();
    }
  }, [pagination.current, pagination.pageSize]);

  // 排序变化时获取数据（不重置页码）
  useEffect(() => {
    // 跳过初始化时的触发
    if (!isInitialized.current) return;

    fetchFees();
  }, [searchParams]);

  // 同步分页状态到 URL
  const updateUrlParams = (newPagination: { current: number; pageSize: number; total?: number }) => {
    const newSearchParams = new URLSearchParams(urlSearchParams);
    newSearchParams.set('page', newPagination.current.toString());
    newSearchParams.set('pageSize', newPagination.pageSize.toString());
    setUrlSearchParams(newSearchParams, { replace: true });
  };

  // 加载每页条数设置
  const loadPageSizeSettings = async () => {
    try {
      const savedPageSize = await userPreferenceService.getPageSizeSettings('fees');
      // 优先使用 URL 参数，如果没有则使用保存的偏好
      const urlPageSize = parseInt(urlSearchParams.get('pageSize') || '0', 10);
      const finalPageSize = urlPageSize > 0 ? urlPageSize : savedPageSize;

      // 只更新 pageSize，不更新 current，避免覆盖 URL 中的页码
      if (pagination.pageSize !== finalPageSize) {
        setPagination(prev => ({
          ...prev,
          pageSize: finalPageSize
        }));
        // 同步 pageSize 到 URL（保持当前页码不变）
        const newSearchParams = new URLSearchParams(urlSearchParams);
        newSearchParams.set('pageSize', finalPageSize.toString());
        setUrlSearchParams(newSearchParams, { replace: true });
      }
    } catch (error) {
      console.error('加载每页条数设置失败:', error);
    }
  };

  // 搜索处理
  const handleSearch = () => {
    const params = {
      search: searchText,
    };
    setSearchParams(params);
    setPagination({ ...pagination, current: 1 });
    fetchFees({ ...params, page: 1 });
  };

  // 表格分页和排序变化
  const handleTableChange = async (paginationConfig: any, filters: any, sorter: any) => {
    const isPageSizeChanged = pagination.pageSize !== paginationConfig.pageSize;

    // 处理排序变化
    let updatedSearchParams = { ...searchParams };
    if (sorter && sorter.field && sorter.order) {
      updatedSearchParams.sort_by = sorter.field;
      updatedSearchParams.sort_direction = sorter.order === 'ascend' ? 'asc' : 'desc';
    } else if (sorter && !sorter.order) {
      // 清除排序
      delete updatedSearchParams.sort_by;
      delete updatedSearchParams.sort_direction;
    }
    setSearchParams(updatedSearchParams);

    // 如果每页条数发生变化，保存用户偏好并从第一页开始
    if (isPageSizeChanged) {
      try {
        await userPreferenceService.savePageSizeSettings('fees', paginationConfig.pageSize);
      } catch (error) {
        console.error('保存每页条数设置失败:', error);
      }

      const newPagination = {
        ...pagination,
        current: 1, // 从第一页开始
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    } else {
      // 只是翻页或排序，更新页码
      const newPagination = {
        ...pagination,
        current: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
      };
      setPagination(newPagination);
      updateUrlParams(newPagination);
    }
  };

  // 计算统计数据（使用 fee_service 与 fee_cost）
  const totalRevenue = fees.reduce((sum, fee) => sum + (parseFloat(String(fee.fee_service || '0'))), 0);
  const totalCost = fees.reduce((sum, fee) => sum + (parseFloat(String(fee.fee_cost || '0'))), 0);
  const totalProfit = totalRevenue - totalCost;

  // 所有可用的列定义
  const allColumnDefinitions: Record<string, any> = {
    service: {
      title: '业务类型',
      dataIndex: 'service',
      key: 'service',
      width: 100,
      render: (type: string) => {
        const option = serviceTypeOptions.find(opt => opt.value === type);
        return <Tag color="blue">{option?.label || type}</Tag>;
      },
    },
    sub_service: {
      title: '子业务名称',
      dataIndex: 'sub_service',
      key: 'sub_service',
      width: 140,
      render: (_: string, record: Fee) => {
        const map = SERVICE_TYPE_MAP[record.service || ''] || {};
        const label = record.sub_service ? (map as any)[record.sub_service] || record.sub_service : '-';
        return label || '-';
      },
    },
    nation: {
      title: '国家',
      dataIndex: 'nation',
      key: 'nation',
      width: 80,
      render: (nation: string) => {
        const option = nationOptions.find(opt => opt.value === nation);
        return option?.label || nation;
      },
    },
    category_type: {
      title: '制度类型',
      dataIndex: 'category_type',
      key: 'category_type',
      width: 160,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    submit_channel: {
      title: '递交渠道',
      dataIndex: 'submit_channel',
      key: 'submit_channel',
      width: 100,
      render: (channel: string) => {
        const option = channelOptions.find(opt => opt.value === channel);
        return option?.label || channel;
      },
    },
    // 联系方式列：展示客户联系邮箱或电话
    contact_info: {
      title: '联系方式',
      dataIndex: 'contact_info',
      key: 'contact_info',
      width: 160,
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    fee_official: {
      title: '官费',
      dataIndex: 'fee_official',
      key: 'fee_official',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    fee_service: {
      title: '服务费',
      dataIndex: 'fee_service',
      key: 'fee_service',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    fee_inquiry: {
      title: '查询费',
      dataIndex: 'fee_inquiry',
      key: 'fee_inquiry',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    fee_notarization: {
      title: '公证费',
      dataIndex: 'fee_notarization',
      key: 'fee_notarization',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    fee_cost: {
      title: '成本',
      dataIndex: 'fee_cost',
      key: 'fee_cost',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    fee_profit: {
      title: '毛利润',
      dataIndex: 'fee_profit',
      key: 'fee_profit',
      width: 100,
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        if (!numFee || isNaN(numFee)) return '-';
        const color = numFee > 0 ? 'green' : numFee < 0 ? 'red' : undefined;
        return <span style={{ color }}>{`¥${numFee.toFixed(2)}`}</span>;
      },
    },
    fee_peer: {
      title: '同行报价',
      dataIndex: 'fee_peer',
      key: 'fee_peer',
      width: 100,
      render: (fee: number | string) => {
        const numFee = typeof fee === 'string' ? parseFloat(fee) : fee;
        return numFee && !isNaN(numFee) ? `¥${numFee.toFixed(2)}` : '-';
      },
    },
    dates_submit: {
      title: '递交周期',
      dataIndex: 'dates_submit',
      key: 'dates_submit',
      width: 100,
    },
    dates_complete: {
      title: '完成周期',
      dataIndex: 'dates_complete',
      key: 'dates_complete',
      width: 120,
    },
    documents: {
      title: '所需资料',
      dataIndex: 'documents_info',
      key: 'documents',
      width: 250,
      render: (documentsInfo: any[], record: Fee) => {
        if (!documentsInfo || documentsInfo.length === 0) return '-';

        return (
          <SimpleDocumentsDisplay
            documentsInfo={documentsInfo}

          />
        );
      },
    },
    remark: {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 200,
      ellipsis: true,
      render: (remark: string) => remark || '-',
    },
    actions: {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_: any, record: Fee) => {
        const actions = [];

        // 查看按钮只在拥有详情权限时显示，避免列表入口与接口权限不一致。
        if (canViewDetail) {
          actions.push(
            <Button
              key="view"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/fees/${record.id}`)}
              size="small"
              title="查看"
            />
          );
        }

        // 编辑按钮 - 根据权限控制
        if (canEdit) {
          actions.push(
            <Button
              key="edit"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              title="编辑"
            />
          );

          // 复制按钮：将记录数据填充到表单，便于快速创建
          actions.push(
            <Button
              key="copy"
              icon={<CopyOutlined />}
              onClick={() => {
                const cloned = { ...record } as any;
                delete cloned.id;
                delete cloned.created_at;
                delete cloned.updated_at;
                setEditingFee(null);
                form.setFieldsValue(cloned);
                setModalVisible(true);
              }}
              size="small"
              title="复制创建"
            />
          );
        }

        // 删除按钮 - 根据权限控制
        if (canDelete) {
          actions.push(
            <Popconfirm
              key="delete"
              title="确定删除这条费用记录吗？"
              onConfirm={() => record.id && handleDelete(record.id)}
              okText="确定"
              cancelText="取消"
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="删除"
              />
            </Popconfirm>
          );
        }

        return <Space>{actions}</Space>;
      },
    },
  };

  // 默认显示的列
  const defaultVisibleColumns = [
    'nation',
    'service',
    'sub_service',
    'category_type',
    'submit_channel',
    'contact_info',
    'fee_official',
    'fee_service',
    'fee_inquiry',
    'fee_cost',
    'fee_profit',
    'fee_peer',
    'dates_submit',
    'dates_complete',
    'documents',
    'remark',
    'actions'
  ];

  // 初始化可见列
  useEffect(() => {
    const loadColumnSettings = async () => {
      try {
        const savedColumns = await userPreferenceService.getColumnSettings('fees');
        const ensureContactPosition = (cols: string[]) => {
          const list = cols.slice();
          const idx = list.indexOf('contact_info');
          if (idx >= 0) {
            list.splice(idx, 1);
            const docIdx = list.indexOf('documents');
            const insertAt = docIdx >= 0 ? docIdx : Math.max(list.indexOf('remark'), 0);
            list.splice(insertAt, 0, 'contact_info');
          }
          return list;
        };

        if (savedColumns && savedColumns.length > 0) {
          setVisibleColumns(ensureContactPosition(savedColumns));
        } else {
          setVisibleColumns(defaultVisibleColumns);
        }
      } catch (error) {
        console.warn('加载列设置失败:', error);
        setVisibleColumns(defaultVisibleColumns);
      }
    };

    loadColumnSettings();
  }, []);

  // 生成表格列，根据权限过滤
  const columns: ColumnsType<Fee> = permissions.getVisibleColumns('fee-management',
    filterActionColumnByAccess(visibleColumns.map(columnKey => ({
      key: columnKey,
      ...allColumnDefinitions[columnKey]
    })), hasRowActionAccess)
  ).map(column => allColumnDefinitions[column.key]).filter(Boolean);

  // 获取可用列配置，根据权限过滤
  const availableColumns = permissions.getVisibleColumns('fee-management',
    filterActionColumnByAccess(Object.keys(allColumnDefinitions).map(key => ({
      key,
      title: allColumnDefinitions[key].title,
      fixed: allColumnDefinitions[key].fixed
    })), hasRowActionAccess)
  );

  // 处理列显示变更
  const handleColumnChange = async (checkedColumns: string[]) => {
    // 保留用户拖拽设置的顺序，只过滤掉 actions（固定在末尾）
    const filteredColumns = checkedColumns.filter(col => col !== 'actions');
    const uniqueColumns = [...filteredColumns, 'actions'];

    setVisibleColumns(uniqueColumns);

    try {
      await userPreferenceService.saveColumnSettings('fees', uniqueColumns);
    } catch (error) {
      console.error('保存列设置失败:', error);
      message.error('保存列设置失败');
    }
  };

  // 处理新增/编辑
  const handleEdit = (fee?: Fee) => {
    setEditingFee(fee || null);
    if (fee) {
      // 文档字段直接赋值，不需要特殊处理
      form.setFieldsValue(fee);
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 处理保存：持久化前确保按规则计算 成本 与 同行报价
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const toNumber = (v: any) => {
        const n = Number(v);
        return isNaN(n) ? 0 : n;
      };
      const round2 = (n: number) => Math.round(n * 100) / 100;

      const official = toNumber(values.fee_official);
      const service = toNumber(values.fee_service);
      const profit = toNumber(values.fee_profit);
      const cost = round2(official + service);
      const peer = round2(cost + profit);

      // 处理文档字段：保存文档ID数组
      const feeData = {
        ...values,
        fee_cost: cost,
        fee_peer: peer,
      };

      if (editingFee) {
        await feeService.updateFee(editingFee.id!, feeData);
        message.success('费用记录更新成功');
      } else {
        await feeService.createFee(feeData);
        message.success('费用记录添加成功');
      }

      setModalVisible(false);
      setEditingFee(null);
      form.resetFields();
      fetchFees();
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 处理删除
  const handleDelete = async (id: number) => {
    if (!id) {
      message.error('费用记录ID无效');
      return;
    }
    try {
      await feeService.deleteFee(id);
      message.success('费用记录删除成功');
      fetchFees();
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  // 批量删除
  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    try {
      const ids = selectedRowKeys as number[];
      const response = await feeService.batchDeleteFees(ids);
      if (response.success) {
        message.success(response.message || `成功删除 ${response.data?.deleted_count || ids.length} 条费用记录`);
        setSelectedRowKeys([]);
        fetchFees();
      } else {
        message.error(response.message || '批量删除失败');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.message || '批量删除失败');
    }
  };

  // 处理导出
  const handleExport = async () => {
    try {
      const blob = await feeService.exportFees(searchParams);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `费用记录数据_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败');
    }
  };

  // 处理导入
  const handleImport = async (file: any) => {
    setImportLoading(true);
    setImportProgress(0);

    try {
      const result = await feeService.importFees(file);

      if (result.success) {
        const { total, success, updated, failed, errors } = result.data;
        let messageText = `导入完成！总计: ${total}, 成功: ${success}, 更新: ${updated}`;

        if (failed > 0) {
          messageText += `, 失败: ${failed}`;
          if (errors && errors.length > 0) {
            messageText += `\n错误详情: ${errors.slice(0, 3).join('; ')}`;
            if (errors.length > 3) {
              messageText += '...';
            }
          }
        }

        if (failed === 0) {
          message.success(messageText);
        } else {
          message.warning(messageText);
        }

        fetchFees(); // 刷新数据
      } else {
        message.error(`导入失败: ${result.message}`);
      }
    } catch (error) {
      console.error('导入失败:', error);
      message.error('导入失败，请检查文件格式');
    } finally {
      setImportLoading(false);
      setImportProgress(0);
      setImportModalVisible(false);
    }
  };

  // 根据权限渲染表单字段的辅助函数
  // 表单字段渲染：保留外部传入的 disabled（只读）优先级
  const renderFormField = (fieldKey: string, component: React.ReactNode): React.ReactNode => {
    // 检查查看权限
    if (!permissions.hasFormFieldAccess('fee-management', fieldKey, 'view')) {
      return null;
    }

    // 检查编辑权限
    const disabled = !permissions.hasFormFieldAccess('fee-management', fieldKey, 'edit');

    // 如果是Form.Item，克隆并添加disabled属性到其children
    if (React.isValidElement(component) && component.type === Form.Item) {
      const children = component.props.children;
      if (React.isValidElement(children)) {
        // 如外部已显式设为 disabled（只读），以其为准
        const childDisabled = (children.props as any)?.disabled;
        return React.cloneElement(component, {
          ...component.props,
          children: React.cloneElement(children as React.ReactElement<any>, { disabled: childDisabled ?? disabled } as any)
        });
      }
    }

    return component;
  };

  // 检查页面访问权限
  if (!permissions.hasPageAccess('fee-management')) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <h3>无权限访问费用管理页面</h3>
        <p>请联系管理员获取相应权限</p>
      </div>
    );
  }

  // 注释：现在使用服务器端分页和搜索，不需要客户端过滤

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={(
          <Space size="small">
            <span>最新报价</span>
            <ListHelpButton page_key="fee_management" />
          </Space>
        )}
        extra={
          <Space>
            <ColumnSelector
              availableColumns={availableColumns}
              visibleColumns={visibleColumns}
              onColumnChange={handleColumnChange}
              fixedColumns={['service', 'actions']}
            />
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => handleEdit()} data-testid="btn-fee-add">
                新增费用
              </Button>
            )}
            {(() => {
              const items: any[] = [];
              if (canExport) items.push({ key: 'export', label: '导出数据', icon: <ExportOutlined />, onClick: handleExport });
              if (canImport) items.push({ key: 'import', label: '导入数据', icon: <ImportOutlined />, onClick: () => setImportModalVisible(true) });
              return items.length > 0 ? (
                <Dropdown menu={{ items }}>
                  <Button data-testid="btn-fee-data-menu">数据管理</Button>
                </Dropdown>
              ) : null;
            })()}
            {canDelete && selectedRowKeys.length > 0 && (
              <Popconfirm
                title={`确定删除选中的 ${selectedRowKeys.length} 条记录吗？`}
                okText="确定"
                cancelText="取消"
                onConfirm={handleBulkDelete}
                disabled={!selectedRowKeys.length}
              >
                <Button danger disabled={!selectedRowKeys.length}>批量删除</Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Input.Search
              placeholder="搜索业务名称、国家、备注"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              allowClear
            />
          </Col>
          <Col span={3}>
            <Select
              placeholder="选择国家"
              style={{ width: '100%' }}
              value={filters.nation || undefined}
              onChange={(value) => handleFilterChange('nation', value || '')}
              allowClear
              showSearch
            >
              {(filterOptions?.nations || []).map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="选择服务名称"
              style={{ width: '100%' }}
              value={filters.service || undefined}
              onChange={(value) => handleFilterChange('service', value || '')}
              allowClear
              showSearch
            >
              {(filterOptions?.services || []).map(option => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={3}>
            <Select
              placeholder="选择递交渠道"
              style={{ width: '100%' }}
              value={filters.submit_channel || undefined}
              onChange={(value) => handleFilterChange('submit_channel', value || '')}
              allowClear
              showSearch
              options={filterOptions.submit_channels}
              optionFilterProp="label"
            />
          </Col>
        </Row>

        {/* 筛选状态显示 */}
        {(Object.values(filters).some(value => value) || searchText) && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{
                padding: '8px 12px',
                backgroundColor: '#f0f2f5',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <Space wrap>
                  <span>已应用筛选条件：</span>
                  {searchText && (
                    <Tag closable onClose={() => setSearchText('')}>
                      搜索: {searchText}
                    </Tag>
                  )}
                  {filters.nation && (
                    <Tag closable onClose={() => handleFilterChange('nation', '')}>
                      国家: {filterOptions.nations.find(opt => opt.value === filters.nation)?.label || filters.nation}
                    </Tag>
                  )}
                  {filters.service && (
                    <Tag closable onClose={() => handleFilterChange('service', '')}>
                      服务名称: {filters.service}
                    </Tag>
                  )}
                  {filters.submit_channel && (
                    <Tag closable onClose={() => handleFilterChange('submit_channel', '')}>
                      递交渠道: {filterOptions.submit_channels.find(opt => opt.value === filters.submit_channel)?.label || filters.submit_channel}
                    </Tag>
                  )}
                  <Button type="link" size="small" onClick={handleResetFilters}>
                    清除所有筛选
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <Table
          columns={columns}
          dataSource={fees}
          loading={loading}
          scroll={{ x: 1600 }}
          sticky={{ offsetHeader: 0 }}
          rowSelection={canDelete ? (rowSelection as any) : undefined}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
          onChange={handleTableChange}
          rowKey="id"
        />
      </Card>

      <Modal
        title={editingFee ? '编辑费用记录' : '新增费用记录'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        width={900}
        destroyOnHidden
        data-testid="modal-fee"
        okButtonProps={{ 'data-testid': 'btn-fee-save' } as any}
        cancelButtonProps={{ 'data-testid': 'btn-fee-cancel' } as any}
      >
        <Form
          form={form}
          layout="vertical"
          // 表单联动：按需自动计算 成本 与 同行报价
          onValuesChange={(changedValues, allValues) => {
            const toNumber = (v: any) => {
              const n = Number(v);
              return isNaN(n) ? 0 : n;
            };
            const round2 = (n: number) => Math.round(n * 100) / 100;

            const official = toNumber(allValues.fee_official);
            const service = toNumber(allValues.fee_service);
            const profit = toNumber(allValues.fee_profit);
            const currentCost = toNumber(allValues.fee_cost);

            // 官费或服务费变化 -> 重新计算 成本 与 同行报价
            if (Object.prototype.hasOwnProperty.call(changedValues, 'fee_official') ||
                Object.prototype.hasOwnProperty.call(changedValues, 'fee_service')) {
              const cost = round2(official + service);
              const peer = round2(cost + profit);
              form.setFieldsValue({ fee_cost: cost, fee_peer: peer });
              return;
            }

            // 毛利润 或 成本 变化 -> 重新计算 同行报价
            if (Object.prototype.hasOwnProperty.call(changedValues, 'fee_profit') ||
                Object.prototype.hasOwnProperty.call(changedValues, 'fee_cost')) {
              const peer = round2(currentCost + profit);
              form.setFieldsValue({ fee_peer: peer });
            }
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              {renderFormField('service',
                <Form.Item
                  label="业务类型"
                  name="service"
                  rules={[{ required: true, message: '请选择业务类型' }]}
                >
                  <Select placeholder="请选择业务类型" data-testid="fee-service">
                    {serviceTypeOptions.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
            <Col span={12}>
              {renderFormField('sub_service',
                <Form.Item
                  label="子业务名称"
                  name="sub_service"
                  rules={[{ required: true, message: '请选择子业务' }]}
                >
                  <Select
                    placeholder={watchedService ? '请选择子业务' : '请先选择业务类型'}
                    disabled={!watchedService}
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {subServiceOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}
            </Col>
              <Col span={12}>
                {renderFormField('nation',
                  <Form.Item
                      label="国家"
                      name="nation"
                      rules={[{ required: true, message: '请选择国家' }]}
                  >
                      <Select
                          placeholder="请选择国家"
                          showSearch
                          filterOption={(input, option) =>
                            String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                          }
                          data-testid="fee-nation"
                      >
                          {nationOptions.map(option => (
                              <Option key={option.value} value={option.label}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
                )}
              </Col>
              <Col span={12}>
                {renderFormField('category_type',
                  <Form.Item label="制度类型" name="category_type">
                      <Select placeholder="请选择制度类型" data-testid="fee-category-type">
                          {categoryOptions.map(option => (
                              <Option key={option.value} value={option.label}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
                )}
              </Col>

            <Col span={12}>
              {renderFormField('submit_channel',
                <Form.Item label="递交渠道" name="submit_channel">
                  <Input placeholder="请输入递交渠道" data-testid="fee-submit-channel" />
                </Form.Item>
              )}
            </Col>

              <Col span={12}>
                {renderFormField('contact_info',
                  <Form.Item label="联系方式" name="contact_info" data-testid="fee-contact-info">
                      <Input placeholder="请输入联系邮箱或电话" data-testid="fee-contact-info-input" />
                  </Form.Item>
                )}
              </Col>

              <Col span={12}>
                {renderFormField('documents',
                  <Form.Item label="所需资料" name="documents">
                      <Select
                          mode="multiple"
                          placeholder="请选择所需资料"
                          showSearch
                          allowClear
                          loading={documentLoading}
                          // 与“国家”下拉保持一致，基于 children 过滤
                          filterOption={(input, option) => String(option?.children || '').toLowerCase().includes(input.toLowerCase())}
                          onSearch={handleDocumentSearch}
                          // 打开下拉即触发加载，避免仅 focus 时未触发导致空白
                          onDropdownVisibleChange={(open) => { if (open) fetchDocumentOptions(); }}
                          onFocus={() => fetchDocumentOptions()}
                          data-testid="fee-documents"
                      >
                          {documentOptions.map(option => (
                              <Option key={option.value} value={option.value}>
                                  {option.label}
                              </Option>
                          ))}
                      </Select>
                  </Form.Item>
                )}
              </Col>

          </Row>

          <Row gutter={16}>
            <Col span={3}>
              {renderFormField('fee_official',
                <Form.Item label="官费" name="fee_official">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    addonBefore="¥"
                    data-testid="fee-amount-official"
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={3}>
              {renderFormField('fee_service',
                <Form.Item label="服务费" name="fee_service">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    addonBefore="¥"
                    data-testid="fee-amount-service"
                  />
                </Form.Item>
              )}
            </Col>
            <Col span={3}>
              <Form.Item label="查询费" name="fee_inquiry">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  addonBefore="¥"
                  data-testid="fee-amount-inquiry"
                />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label="公证费" name="fee_notarization">
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0.00"
                  min={0}
                  precision={2}
                  addonBefore="¥"
                  data-testid="fee-amount-notarization"
                />
              </Form.Item>
            </Col>
              <Col span={4}>
                <Form.Item label="成本" name="fee_cost">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    addonBefore="¥"
                    data-testid="fee-amount-cost"
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="毛利润" name="fee_profit">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    addonBefore="¥"
                    data-testid="fee-amount-profit"
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="同行报价" name="fee_peer">
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    min={0}
                    precision={2}
                    addonBefore="¥"
                    data-testid="fee-amount-peer"
                    disabled
                  />
                </Form.Item>
              </Col>
          </Row>

          <Row gutter={16}>
              <Col span={12}>
                  <Form.Item label="递交周期" name="dates_submit">
                      <Input placeholder="如：3-5工作日" data-testid="fee-dates-submit" />
                  </Form.Item>
              </Col>
              <Col span={12}>
                  <Form.Item label="完成周期" name="dates_complete">
                      <Input placeholder="如：12-18个月" data-testid="fee-dates-complete" />
                  </Form.Item>
              </Col>
          </Row>

          <Form.Item label="备注" name="remark">
            <TextArea rows={3} placeholder="请输入备注信息" data-testid="fee-remark" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 导入模态框 */}
      <Modal
        title="导入费用记录"
        open={importModalVisible}
        onCancel={() => {
          setImportModalVisible(false);
          setImportProgress(0);
        }}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Upload.Dragger
            name="file"
            accept=".xlsx,.xls,.csv"
            beforeUpload={(file) => {
              handleImport(file);
              return false; // 阻止自动上传
            }}
            showUploadList={false}
            disabled={importLoading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
            <p className="ant-upload-hint">
              支持 .xlsx, .xls, .csv 格式文件，单个文件不超过10MB
            </p>
          </Upload.Dragger>

          {importLoading && (
            <div style={{ marginTop: 20 }}>
              <Progress percent={importProgress} />
              <p>导入中，请稍候...</p>
            </div>
          )}

          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <ul style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
              <li>请使用提供的模板格式进行数据整理</li>
              <li>订单编号为必填项，用于判断记录唯一性</li>
              <li>重复的订单编号将更新现有数据，新订单编号将被添加</li>
              <li>金额字段支持数字格式，系统会自动处理</li>
              <li>日期字段请使用 YYYY-MM-DD 格式</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FeeManagement;
